import { Operation, Team, AuditLog } from '../types';
import api from './api';

export const operationService = {
  async getActiveOperations(orgIDs: number[]): Promise<Operation[]> {
    try {
      const currentYear = new Date().getFullYear();
      const requestBody = {
        orgID: orgIDs.map(orgID => ({ orgID }))
      };
      
      const response = await api.post(`/api/v1/operations/${currentYear}`, requestBody);
      
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Einsätze konnten nicht abgerufen werden');
    }
  },

  async getOperationsByYear(orgIDs: number[], year: number): Promise<Operation[]> {
    try {
      const requestBody = {
        orgID: orgIDs[0] // Send single orgID as expected by the API
      };
      
      const response = await api.post(`/api/v1/operations/${year}`, requestBody);
      
      // Get teams data for all operations
      const operationsWithTeams = await this.enrichOperationsWithTeams(response.data);
      
      return operationsWithTeams;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Einsätze konnten nicht abgerufen werden');
    }
  },

  async enrichOperationsWithTeams(operations: Operation[]): Promise<Operation[]> {
    try {
      // Get all teams for the organization
      const teamsEndpoint = '/api/v1/teams/by-org';
      const orgID = operations[0]?.orgID; // Get orgID from first operation
      
      if (!orgID) {
        return operations.map(operation => ({
          ...operation,
          asm_teams: false
        }));
      }
      
      const teamsResponse = await api.post(teamsEndpoint, { orgID });
      const allTeams = teamsResponse.data;
      
      // Enrich operations with ASM teams information
      const enrichedOperations = operations.map(operation => {
        // Check if there's a team entry with matching opsID
        const hasAsmTeams = allTeams.some((team: any) => team.opsID === operation.id);
        
        return {
          ...operation,
          asm_teams: hasAsmTeams
        };
      });
      
      return enrichedOperations;
    } catch (error: any) {
      // Return original operations if enrichment fails
      return operations.map(operation => ({
        ...operation,
        asm_teams: false
      }));
    }
  },

  async getOperation(orgID: number, opsID: number): Promise<Operation[]> {
    try {
      const requestBody = {
        orgID: orgID,
        opsID: opsID
      };
      
      const response = await api.post('/api/v1/operation', requestBody);
      
      // Enrich operation with ASM teams information
      const enrichedOperations = await this.enrichOperationsWithTeams(response.data);
      
      return enrichedOperations;
    } catch (error: any) {
      // Show more detailed error information
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        if (typeof errorData === 'object' && errorData.message) {
          throw new Error(`Datenbankfehler: ${errorData.message}`);
        } else if (typeof errorData === 'string') {
          throw new Error(`Datenbankfehler: ${errorData}`);
        } else {
          throw new Error(`Datenbankfehler: ${JSON.stringify(errorData)}`);
        }
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Einsatzdetails konnten nicht abgerufen werden');
      }
    }
  },

  async getTeamsByOperation(orgID: number, opsID: number): Promise<Team[]> {
    try {
      const response = await api.post('/api/v1/teams/by-operation', {
        orgID: orgID,
        opsID: opsID
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching teams:', error);
      return [];
    }
  },

  async getAuditLogsByOperation(orgID: number, opsID: number): Promise<AuditLog[]> {
    try {
      const response = await api.get(`/api/v1/auditlog/${opsID}`);
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  },

  async generateReport(orgID: number, opsID: number): Promise<Blob> {
    try {
      // Get operation details
      const operationData = await this.getOperation(orgID, opsID);
      if (!operationData || operationData.length === 0) {
        throw new Error('Einsatz nicht gefunden');
      }
      
      const operation = operationData[0];
      
      // Get teams and audit logs
      const teams = await this.getTeamsByOperation(orgID, opsID);
      const auditLogs = await this.getAuditLogsByOperation(orgID, opsID);
      
      // Generate PDF using the enhanced generator
      const { generateOperationReport } = await import('../utils/pdfGenerator');
      const doc = generateOperationReport(operation, teams, auditLogs);
      
      // Convert to blob
      const pdfBlob = doc.output('blob');
      return pdfBlob;
    } catch (error: any) {
      throw new Error('Bericht konnte nicht generiert werden');
    }
  },

  async deleteOperation(orgID: number, opsID: number): Promise<any> {
    try {
      const response = await api.post(`/api/v1/operations/${opsID}/delete`, {
        orgID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting operation:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Einsatz konnte nicht gelöscht werden');
    }
  },

  async openOperation(orgID: number, opsID: number): Promise<any> {
    try {
      const response = await api.post(`/api/v1/operations/${opsID}/open`, {
        orgID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error opening operation:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Einsatz konnte nicht geöffnet werden');
    }
  },

  async closeOperation(orgID: number, opsID: number): Promise<any> {
    try {
      const response = await api.post(`/api/v1/operations/${opsID}/close`, {
        orgID
      });
      return response.data;
    } catch (error: any) {
      console.error('Error closing operation:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Einsatz konnte nicht geschlossen werden');
    }
  },

  async sealOperation(orgID: number, opsID: number): Promise<any> {
    try {
      const response = await api.post(`/api/v1/operations/${opsID}/seal`, {
        orgID,
        lockStatus: 2
      });
      return response.data;
    } catch (error: any) {
      console.error('Error sealing operation:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Einsatz konnte nicht versiegelt werden');
    }
  }
}; 